import React from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import {
   getCreditPackageTiers,
   getCreditReleaseStats,
   type CreditPackageTier,
} from '@/api/vendo';

const AddCredits = () => {
   const { handleShow, handleClose } = useUIStore();

   const { data: packages = [], isLoading } = useQuery({
      queryKey: ['vendo', 'credit-package-tiers'],
      queryFn: getCreditPackageTiers,
   });

   const { data: releaseStats } = useQuery({
      queryKey: ['vendo', 'credit-release-stats'],
      queryFn: getCreditReleaseStats,
   });

   const handlePick = (pkg: CreditPackageTier) => {
      handleClose();
      handleShow({
         template: 'buy-credit-package',
         title: `Buy ${pkg.name} pack — ${pkg.credits} credits`,
         windowType: 'desktop',
         data: {
            tier: pkg.name,
            credits: pkg.credits,
            amount: pkg.amount,
            pricePerCredit: pkg.pricePerCredit,
            label: pkg.name,
         },
      });
   };

   // 0 monthlyLimit = unlimited (don't gate anything). If the stats fetch
   // hasn't returned yet, optimistically allow — the server enforces the
   // real check on initiate.
   const isOverCap = (pkg: CreditPackageTier) => {
      if (!releaseStats || releaseStats.monthlyLimit === 0) return false;
      return pkg.credits > releaseStats.availableToRelease;
   };

   return (
      <Wrapper>
         <Intro>
            <IntroTitle>Add Credits</IntroTitle>
            <IntroText>
               Pick a package — pay off-platform, then submit proof. Admin
               approves and credits land in your wallet.
            </IntroText>
            {releaseStats && releaseStats.monthlyLimit > 0 && (
               <CapHint>
                  Monthly cap headroom:{' '}
                  <strong>{releaseStats.availableToRelease.toLocaleString()}</strong>{' '}
                  of {releaseStats.monthlyLimit.toLocaleString()} credits left this
                  month. Larger packs are disabled when they exceed the headroom.
               </CapHint>
            )}
         </Intro>

         {isLoading && <SkeletonLines lines={3} />}
         {!isLoading && packages.length === 0 && (
            <EmptyHint>No packages available — contact admin.</EmptyHint>
         )}

         <PkgList>
            {packages.map((pkg) => {
               const overCap = isOverCap(pkg);
               return (
                  <PkgRow
                     key={pkg.name}
                     $disabled={overCap}
                     onClick={() => {
                        if (overCap) return;
                        handlePick(pkg);
                     }}
                  >
                     <PkgIcon>{pkg.icon || '📦'}</PkgIcon>
                     <PkgBody>
                        <PkgName>{pkg.name}</PkgName>
                        <PkgSub>
                           {pkg.credits} credits · ₱{pkg.pricePerCredit}/credit
                           {pkg.savings ? ` · save ${pkg.savings}%` : ''}
                           {overCap && ' · exceeds monthly cap'}
                        </PkgSub>
                     </PkgBody>
                     <PkgTotal>₱{pkg.amount.toLocaleString()}</PkgTotal>
                     <PkgArrow>{overCap ? '✕' : '→'}</PkgArrow>
                  </PkgRow>
               );
            })}
         </PkgList>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 480px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 14px;
   max-height: 70vh;
   overflow-y: auto;
   @media (max-width: 768px) { width: 100%; max-height: 80vh; }
`;

const Intro = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   background: var(--app-brand-tint);
`;

const IntroTitle = styled.p`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
   margin-bottom: 4px;
`;

const IntroText = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const EmptyHint = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   padding: 16px;
   text-align: center;
`;

const PkgList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const PkgRow = styled.button<{ $disabled?: boolean }>`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 10px 14px;
   border: 1px solid ${({ $disabled }) =>
      $disabled ? colors.GRAY13 : colors.GRAY12};
   background: ${({ $disabled }) =>
      $disabled ? colors.GRAY14 : 'var(--app-surface)'};
   border-radius: 8px;
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
   text-align: left;
   font-family: inherit;
   transition: all 120ms ease;
   &:hover {
      border-color: ${({ $disabled }) =>
         $disabled ? colors.GRAY13 : colors.PRIMARY};
      background: ${({ $disabled }) =>
         $disabled ? colors.GRAY14 : `${colors.PRIMARY}06`};
   }
`;

const CapHint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   margin-top: 6px;
   line-height: 1.5;
   strong { color: ${colors.MAIN_DARK}; font-weight: 800; }
`;

const PkgIcon = styled.span`
   font-size: 22px;
   flex-shrink: 0;
   width: 32px;
   text-align: center;
`;

const PkgBody = styled.div`
   flex: 1;
   min-width: 0;
`;

const PkgName = styled.p`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const PkgSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const PkgTotal = styled.span`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.PRIMARY};
   flex-shrink: 0;
`;

const PkgArrow = styled.span`
   font-size: 16px;
   color: ${colors.GRAY5};
   flex-shrink: 0;
`;

export default AddCredits;
